import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { Badge, Text, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import type { Participant, CohortStatus } from '../../../types';
import { participantsApi } from '../../../services/api/participants';
import { cohortsApi } from '../../../services/api/cohorts';
import { groupsApi } from '../../../services/api/groups';
import { ParticipantDetailModal } from '../../../pages/cohorts/components/ParticipantDetailModal';
import classes from './ParticipantEmailPill.module.css';

interface CohortGroup {
  id: string;
  name: string;
  coach_name?: string;
}

interface ParticipantDetailContextValue {
  openParticipantDetail: (participantId: string, cohortId: string) => void;
}

const ParticipantDetailContext = createContext<ParticipantDetailContextValue | null>(null);

function useParticipantDetail() {
  const ctx = useContext(ParticipantDetailContext);
  if (!ctx) throw new Error('useParticipantDetail must be used within ParticipantDetailProvider');
  return ctx;
}

interface ModalState {
  participant: Participant;
  cohortId: string;
  cohortStatus?: CohortStatus;
  groups: CohortGroup[];
}

interface ParticipantDetailProviderProps {
  children: ReactNode;
  /** MED-764: When set, auto-open this participant's detail modal on mount */
  initialParticipantId?: string;
  /** MED-764: Required cohort ID when initialParticipantId is set */
  initialCohortId?: string;
}

export function ParticipantDetailProvider({ children, initialParticipantId, initialCohortId }: ParticipantDetailProviderProps) {
  const [modalState, setModalState] = useState<ModalState | null>(null);
  const [opened, { open, close }] = useDisclosure(false);
  const [loading, setLoading] = useState(false);

  const openParticipantDetail = useCallback(async (participantId: string, cohortId: string) => {
    if (loading) return;
    setLoading(true);
    try {
      const [participant, cohort, groupsResult] = await Promise.all([
        participantsApi.getById(cohortId, participantId),
        cohortsApi.getById(cohortId),
        groupsApi.list(cohortId, { limit: 200 }),
      ]);
      setModalState({
        participant,
        cohortId,
        cohortStatus: cohort.status,
        groups: groupsResult.data.map(g => ({ id: g.id, name: g.name, coach_name: g.coach_name })),
      });
      open();
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Could not load participant details',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [loading, open]);

  // MED-764: Auto-open participant detail on mount when initial IDs are provided
  const autoOpenFired = useRef(false);
  useEffect(() => {
    if (initialParticipantId && initialCohortId && !autoOpenFired.current) {
      autoOpenFired.current = true;
      openParticipantDetail(initialParticipantId, initialCohortId);
    }
  }, [initialParticipantId, initialCohortId, openParticipantDetail]);

  const handleClose = useCallback(() => {
    close();
    setModalState(null);
  }, [close]);

  return (
    <ParticipantDetailContext.Provider value={{ openParticipantDetail }}>
      {children}
      {modalState && (
        <ParticipantDetailModal
          opened={opened}
          onClose={handleClose}
          participant={modalState.participant}
          cohortId={modalState.cohortId}
          cohortStatus={modalState.cohortStatus}
          groups={modalState.groups}
          onUpdate={handleClose}
          onDelete={handleClose}
        />
      )}
    </ParticipantDetailContext.Provider>
  );
}

interface ParticipantEmailPillProps {
  email: string;
  participantId: string;
  cohortId: string;
}

export function ParticipantEmailPill({ email, participantId, cohortId }: ParticipantEmailPillProps) {
  const { openParticipantDetail } = useParticipantDetail();

  return (
    // The pill can be cut in a narrow column, so its Tooltip carries the full email.
    <Tooltip
      label={
        <>
          <Text size="xs" fw={600}>{email}</Text>
          <Text size="xs">View participant details</Text>
        </>
      }
      withArrow
      position="top"
    >
      <Badge
        component="button"
        type="button"
        size="sm"
        variant="light"
        color="gray"
        className={classes.pill}
        aria-label={`View details for ${email}`}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          openParticipantDetail(participantId, cohortId);
        }}
        styles={{
          label: {
            fontSize: 11,
            textTransform: 'none' as const,
            letterSpacing: 0.3,
          },
        }}
      >
        {email}
      </Badge>
    </Tooltip>
  );
}
